import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'
import { environment } from '@attain-apps/fe/web/main/env'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as components from './components'
import { RouteParam } from './route-param'
import { ROUTE_BOUND_PARAMS } from './route-reuse-strategy'
import * as state from './state'

/**
 * Max amount of time, in milliseconds, before we fallback on a hard-coded title
 */
const MAX_TIME_FOR_TITLE_RETRIEVAL = 100

type SupportedSelectorName = Satisfies<
  'tenant' | 'zone' | 'app' | 'appPage' | 'scenario',
  keyof state.me.subfeatures.contexts.Feature['selectors']
>
type SelectedEntity<TSelectorName extends SupportedSelectorName> = NonNullable<
  ReturnType<
    state.me.subfeatures.contexts.Feature['selectors'][TSelectorName]['asSelector']
  >
>

/**
 * Fallback title used when a dynamic title cannot be generated
 */
const DEFAULT_TITLE = 'Attain Apps'

/**
 * Formats a route title with the application suffix.
 */
function _getTitle(pageDescription: string): string {
  return `${pageDescription} - Attain Apps`
}

const _onlyShowLandingWhenSettled: ngRouter.CanActivateFn = route => {
  const meFeature = ngCore.inject(state.me.Feature)

  return meFeature.contexts.selectors
    .getPlatformLandingRouteDecision(route.paramMap.get(RouteParam.TenantAlias))
    .asObservable.pipe(
      // The landing page is our fallback. While the current user snapshot is
      // still loading, wait instead of briefly activating the no-access page.
      rxjs.filter(({ isSettled }) => isSettled),
      // Angular needs one guard result for this navigation. Future context
      // changes should start a new navigation/effect pass, not keep this guard
      // subscription alive.
      rxjs.take(1),
      rxjs.map(({ url }) => (url ? _redirectTo(url) : true)),
    )
}

/**
 * Redirects without leaving the incomplete landing route in browser history.
 */
function _redirectTo(url: ngRouter.UrlTree): ngRouter.RedirectCommand {
  return new ngRouter.RedirectCommand(url, {
    replaceUrl: true,
  })
}

/**
 * Resolves a route title from selected user context entities.
 */
function _getTitle$<const TNames extends readonly SupportedSelectorName[]>(
  names: TNames,
  producer: (
    ...entities: {
      readonly [I in keyof TNames]: SelectedEntity<TNames[I]>
    }
  ) => string,
): ngRouter.ResolveFn<string>
/**
 * Resolves a route title from selected user context entities.
 */
function _getTitle$(
  selectorNames: readonly SupportedSelectorName[],
  producer: (
    ...entities: readonly SelectedEntity<SupportedSelectorName>[]
  ) => string,
): ngRouter.ResolveFn<string> {
  return currentRoute => {
    const meFeature = ngCore.inject(state.me.Feature)
    return rxjs
      .combineLatest(
        selectorNames.map(selectorName =>
          meFeature.contexts.selectors[selectorName].asObservable.pipe(
            // @ts-expect-error TEMPORARY
            rxjs.filter(TypeChecker.isNotNullish.raw),
          ),
        ),
      )
      .pipe(
        // @ts-expect-error TEMPORARY
        rxjs.map(entities => _getTitle(producer(...entities))),
        // If we don't get the title within this time frame, we stop trying, and
        // instead leave the title as the site's default
        rxjs.timeout({
          first: MAX_TIME_FOR_TITLE_RETRIEVAL,
          with: () => rxjs.of(DEFAULT_TITLE),
        }),
      )
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Routes                                   */
/* -------------------------------------------------------------------------- */
export const ROUTES: ngRouter.Routes = [
  {
    canActivate: [_onlyShowLandingWhenSettled],
    component: components.platform.containers.landing.Component,
    path: '',
    pathMatch: 'full',
  },
  // Not included in production
  ...(!environment.production
    ? [
        {
          component: graphql.components.sandbox.Component,
          path: 'graphql',
          title: _getTitle('GraphQL Sandbox'),
        },
      ]
    : []),
  /* -------------------------------------------------------------------------- */
  /*                                    Main                                    */
  /* -------------------------------------------------------------------------- */
  // This componentless route is the zone scope. "Scope" here means Angular
  // route-injector lifetime, not a visual layout or page concept.
  //
  // Home lives directly under this scope, so rendering app cards at
  // `/portal/:tenantAlias/:zoneAlias` does not create app-owned state.
  {
    path: `portal/:${RouteParam.TenantAlias}/:${RouteParam.ZoneAlias}`,
    data: {
      // Keep this route branch alive while tenant/zone stay the same. Changing
      // either value recreates the branch and destroys route-bound descendants.
      [ROUTE_BOUND_PARAMS]: [RouteParam.TenantAlias, RouteParam.ZoneAlias],
    },
    children: [
      // Zone home is intentionally outside any app scope. It can show links into
      // apps without instantiating their providers or entity collections.
      {
        component: components.platform.containers.home.Component,
        path: '',
        pathMatch: 'full',
        title: _getTitle$(
          ['tenant', 'zone'],
          (tenant, zone) => `Home - ${tenant.code} (${zone.code})`,
        ),
      },
      {
        // This route is the route-bound app scope. The boundary component owns
        // the app-scoped providers because its component injector is recreated
        // with the active app branch.
        component: components.platform.containers.app.routeBoundary.Component,
        path: `:${RouteParam.AppAlias}`,
        data: {
          // Page changes remain inside the same app scope. Changing tenant,
          // zone, or app alias recreates the app route injector.
          [ROUTE_BOUND_PARAMS]: [
            RouteParam.TenantAlias,
            RouteParam.ZoneAlias,
            RouteParam.AppAlias,
          ],
        },
        children: [
          // The app landing is inside the app scope, so it can use route-bound
          // state without forcing that state to exist on zone home.
          {
            component: components.platform.containers.app.landing.Component,
            path: '',
            pathMatch: 'full',
            title: _getTitle$(
              ['app', 'tenant', 'zone'],
              (app, tenant, zone) =>
                `${app.name} - ${tenant.code} (${zone.code})`,
            ),
          },
          // App pages are children of the app scope. Navigating between pages of
          // the same app keeps the scoped providers alive; switching apps does
          // not.
          {
            component: components.platform.containers.app.page.Component,
            path: `:${RouteParam.AppPageAlias}`,
            // TODO: get app & app page's name & tenant & zones's codes (e.g. 'Rate Rules - UNC Main - Attain Apps')
            title: _getTitle$(
              ['app', 'appPage', 'tenant', 'zone'],
              (app, appPage, tenant, zone) =>
                `${app.name} | ${appPage.name} - ${tenant.code} (${zone.code})`,
            ),
          },
        ],
      },
    ],
  },
  {
    canActivate: [_onlyShowLandingWhenSettled],
    component: components.platform.containers.landing.Component,
    path: `portal/:${RouteParam.TenantAlias}`,
    title: _getTitle$(['tenant'], tenant => tenant.code),
  },
  /* -------------------------------------------------------------------------- */
  /*                                    Admin                                   */
  /* -------------------------------------------------------------------------- */
  {
    component: components.platform.containers.admin.routeBoundary.Component,
    path: 'admin',
    children: [
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.scenario.Component,
        data: state.admin.portalAccess.RouteData.scenario,
        path: `tenants/:${RouteParam.TenantId}/:${RouteParam.ZoneId}/:${RouteParam.AppId}/:${RouteParam.ScenarioId}`,
        // TODO: get scenario & app's name & tenant & zone's codes
        title: () => _getTitle('Admin - Scenario'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.app.Component,
        data: state.admin.portalAccess.RouteData.app,
        path: `tenants/:${RouteParam.TenantId}/:${RouteParam.ZoneId}/:${RouteParam.AppId}`,
        // TODO: get app's name & tenant & zone's codes
        title: () => _getTitle('Admin - App'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.zone.Component,
        data: state.admin.portalAccess.RouteData.zone,
        path: `tenants/:${RouteParam.TenantId}/:${RouteParam.ZoneId}`,
        // TODO: get tenant & zone's code
        title: () => _getTitle('Admin - Zone'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.tenant.Component,
        data: state.admin.portalAccess.RouteData.tenant,
        path: `tenants/:${RouteParam.TenantId}`,
        // TODO: get tenant's code
        title: () => _getTitle('Admin - Tenant'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.tenants.Component,
        data: state.admin.portalAccess.RouteData.adminCenter,
        path: 'tenants',
        title: _getTitle('Admin - Tenants'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component: components.platform.containers.admin.users.Component,
        data: state.admin.portalAccess.RouteData.platformUsers,
        path: 'users',
        title: _getTitle('Admin - Users'),
      },
      {
        canActivate: [state.admin.portalAccess.canActivateAdminRoute],
        component:
          components.platform.containers.admin.knowledgeBases.Component,
        data: state.admin.portalAccess.RouteData.platformKnowledgeBases,
        path: 'knowledge-bases',
        title: _getTitle('Admin - Knowledge Bases'),
      },
    ],
  },
]
